import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  FileText,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  User,
  Building,
  Hammer,
  List,
  Info,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

export default function BossCreateDayWork({
  visible,
  onClose,
  onSuccess,
  places = [],
  employees = [],
  preselectedDate = "",
  preselectedPlaceId = null,
  preselectedEmployees = [],
}) {
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPlacePicker, setShowPlacePicker] = useState(false);
  const [showEmployeePicker, setShowEmployeePicker] = useState(false);
  const [selectedPlaceType, setSelectedPlaceType] = useState(null);
  const [userData, setUserData] = useState(null);
  const [allData, setAllData] = useState(null);

  // Load data from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    const data = localStorage.getItem("allData");

    if (user) setUserData(JSON.parse(user));
    if (data) setAllData(JSON.parse(data));
  }, []);

  // Get today's date in DD-MM-YYYY format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  // Set date, place, and employees when modal opens
  useEffect(() => {
    if (visible && allData) {
      setDate(preselectedDate || getTodayDate());
      setSelectedPlaceId(preselectedPlaceId);

      // Load employees with their existing notes
      if (preselectedEmployees && preselectedEmployees.length > 0) {
        const employeesWithNotes = preselectedEmployees.map((emp) => {
          const existingWork = allData.myDailyWork?.find(
            (work) =>
              work.employeeId === emp.id &&
              work.date === (preselectedDate || getTodayDate()) &&
              work.placeId === preselectedPlaceId
          );

          return {
            id: emp.id,
            name: emp.name,
            salary: emp.salary?.toString() || "",
            note: existingWork?.note || emp.note || "",
          };
        });

        setSelectedEmployees(employeesWithNotes);
      } else {
        setSelectedEmployees([]);
      }
    }
  }, [
    visible,
    preselectedDate,
    preselectedPlaceId,
    preselectedEmployees,
    allData,
  ]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedEmployees([]);
    setDate("");
    setNote("");
    setSelectedPlaceType(null);
    onClose();
  };

  const toggleEmployeeSelection = (employee) => {
    const isSelected = selectedEmployees.find((e) => e.id === employee.id);

    if (isSelected) {
      setSelectedEmployees(
        selectedEmployees.filter((e) => e.id !== employee.id)
      );
    } else {
      setSelectedEmployees([
        ...selectedEmployees,
        {
          id: employee.id,
          name: employee.name,
          salary: employee.salary?.toString() || "",
          note: "",
        },
      ]);
    }
  };

  const updateEmployeeSalary = (employeeId, newSalary) => {
    setSelectedEmployees(
      selectedEmployees.map((emp) =>
        emp.id === employeeId ? { ...emp, salary: newSalary } : emp
      )
    );
  };

  const updateEmployeeNote = (employeeId, newNote) => {
    setSelectedEmployees(
      selectedEmployees.map((emp) =>
        emp.id === employeeId ? { ...emp, note: newNote } : emp
      )
    );
  };

  const removeEmployee = (employeeId) => {
    setSelectedEmployees(selectedEmployees.filter((e) => e.id !== employeeId));
  };

  const handleCreateDayWork = async () => {
    // Validation
    if (!date || date.trim() === "") {
      alert("Please enter a date");
      return;
    }

    if (!selectedPlaceId) {
      alert("Please select a place");
      return;
    }

    const invalidSalary = selectedEmployees.find(
      (emp) => !emp.salary || isNaN(emp.salary)
    );

    if (invalidSalary) {
      alert(`Please enter a valid salary for ${invalidSalary.name}`);
      return;
    }

    if (!userData) {
      alert("User data not found");
      return;
    }

    setIsLoading(true);

    try {
      const data = {
        UserId: userData.id,
        PlaceId: selectedPlaceId,
        Date: date,
        Note: note.trim(),
        Employees: selectedEmployees.map((emp) => ({
          EmployeeId: emp.id,
          Salary: parseFloat(emp.salary),
          Note: emp.note || "",
        })),
        WhoMake: userData.id,
      };

      const response = await axios.post(
        `${MyDomain}DailyWork/CreateDayWork`,
        data
      );

      if (response.status === 200) {
        // Update localStorage with new data
        localStorage.setItem("allData", JSON.stringify(response.data));
        alert("Work entry created successfully!");
        handleClose();
        if (onSuccess) onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error creating day work:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to create work entry";
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !allData) return null;

  // Filter places based on selected type
  const filteredPlaces = selectedPlaceType
    ? allData.myPlaces?.filter(
        (place) => place.placeType === selectedPlaceType && place.active === 1
      ) || []
    : allData.myPlaces?.filter((place) => place.active === 1) || [];

  const selectedPlace = allData.myPlaces?.find((p) => p.id === selectedPlaceId);
  const isFormValid = date && selectedPlaceId && selectedEmployees.length > 0;

  const getManagerName = (managerId) => {
    const manager = allData.myEmployees?.find((e) => e.id === managerId);
    return manager?.name || "Me";
  };

  const activeEmployees =
    allData.myEmployees?.filter((emp) => emp.active === 1) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 pt-4 pb-5">
          <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden"></div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Day Work</h2>
                <p className="text-sm text-gray-600">
                  {preselectedEmployees.length > 0
                    ? "Update assignments"
                    : "Create new entry"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Date Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3">
                  <Calendar className="w-5 h-5 text-gray-600 mr-3" />
                  <input
                    type="text"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                </div>
                <button
                  onClick={() => setDate(getTodayDate())}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl transition font-semibold"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Today</span>
                </button>
              </div>
            </div>

            {/* Place Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Place <span className="text-red-500">*</span>
              </label>

              {/* Place Type Filter */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => setSelectedPlaceType(null)}
                  className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition font-semibold text-sm ${
                    selectedPlaceType === null
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span>All</span>
                </button>
                <button
                  onClick={() => setSelectedPlaceType(1)}
                  className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition font-semibold text-sm ${
                    selectedPlaceType === 1
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Building className="w-4 h-4" />
                  <span>Permanent</span>
                </button>
                <button
                  onClick={() => setSelectedPlaceType(2)}
                  className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition font-semibold text-sm ${
                    selectedPlaceType === 2
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Hammer className="w-4 h-4" />
                  <span>Temporary</span>
                </button>
              </div>

              <button
                onClick={() => setShowPlacePicker(!showPlacePicker)}
                className={`w-full flex items-center justify-between bg-gray-50 border-2 rounded-xl px-4 py-3 transition ${
                  showPlacePicker
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-600" />
                  <span
                    className={
                      selectedPlace ? "text-gray-900" : "text-gray-500"
                    }
                  >
                    {selectedPlace ? selectedPlace.place : "Select Place"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {filteredPlaces.length > 0 && (
                    <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded">
                      {filteredPlaces.length}
                    </span>
                  )}
                  {showPlacePicker ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              </button>

              {showPlacePicker && (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                  {filteredPlaces.length === 0 ? (
                    <div className="p-8 text-center">
                      <Building className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No places found</p>
                    </div>
                  ) : (
                    filteredPlaces.map((place) => (
                      <button
                        key={place.id}
                        onClick={() => {
                          setSelectedPlaceId(place.id);
                          setShowPlacePicker(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition border-b border-gray-100 last:border-0 ${
                          selectedPlaceId === place.id ? "bg-blue-50" : ""
                        }`}
                      >
                        {selectedPlaceId === place.id ? (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        ) : place.placeType === 1 ? (
                          <Building className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Hammer className="w-5 h-5 text-gray-400" />
                        )}
                        <div className="flex-1 text-left">
                          <p
                            className={`font-medium ${
                              selectedPlaceId === place.id
                                ? "text-blue-600"
                                : "text-gray-900"
                            }`}
                          >
                            {place.place}
                          </p>
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              place.placeType === 1
                                ? "bg-blue-100 text-blue-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {place.placeType === 1 ? "Permanent" : "Temporary"}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Employee Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employees <span className="text-red-500">*</span>
              </label>

              <button
                onClick={() => setShowEmployeePicker(!showEmployeePicker)}
                className={`w-full flex items-center justify-between bg-gray-50 border-2 rounded-xl px-4 py-3 transition ${
                  showEmployeePicker
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-gray-600" />
                  <span
                    className={
                      selectedEmployees.length > 0
                        ? "text-gray-900"
                        : "text-gray-500"
                    }
                  >
                    {selectedEmployees.length > 0
                      ? `${selectedEmployees.length} Selected`
                      : "Select Employees"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {selectedEmployees.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                      {selectedEmployees.length}
                    </span>
                  )}
                  {showEmployeePicker ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              </button>

              {showEmployeePicker && (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                  {activeEmployees.map((employee) => {
                    const isSelected = selectedEmployees.find(
                      (e) => e.id === employee.id
                    );
                    return (
                      <button
                        key={employee.id}
                        onClick={() => toggleEmployeeSelection(employee)}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition border-b border-gray-100 last:border-0 ${
                          isSelected ? "bg-blue-50" : ""
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                            isSelected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span
                          className={`font-medium ${
                            isSelected ? "text-blue-600" : "text-gray-900"
                          }`}
                        >
                          {employee.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected Employees List */}
            {selectedEmployees.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                  <List className="w-5 h-5 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-700">
                    Assign Salaries & Notes ({selectedEmployees.length})
                  </h3>
                </div>

                <div className="space-y-3">
                  {selectedEmployees.map((employee, index) => {
                    const existingWork = allData.myDailyWork?.find(
                      (work) =>
                        work.employeeId === employee.id &&
                        work.date === date &&
                        work.placeId === selectedPlaceId
                    );

                    return (
                      <div
                        key={employee.id}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-bold">
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {employee.name}
                              </p>
                              {existingWork?.whoMake && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Shield className="w-3 h-3 text-purple-600" />
                                  <span className="text-xs text-purple-600 font-medium">
                                    {getManagerName(existingWork.whoMake)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeEmployee(employee.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition"
                          >
                            <X className="w-5 h-5 text-red-500" />
                          </button>
                        </div>

                        {/* Salary Input */}
                        <div className="flex items-center gap-2 bg-white rounded-lg p-3 border border-gray-200 mb-2">
                          <DollarSign className="w-4 h-4 text-gray-600" />
                          <input
                            type="number"
                            value={employee.salary}
                            onChange={(e) =>
                              updateEmployeeSalary(employee.id, e.target.value)
                            }
                            placeholder="Enter salary"
                            className="flex-1 outline-none text-gray-900"
                          />
                          <span className="text-gray-600 font-semibold">₪</span>
                        </div>

                        {/* Note Input */}
                        <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-gray-200">
                          <FileText className="w-4 h-4 text-gray-600 mt-1" />
                          <textarea
                            value={employee.note}
                            onChange={(e) =>
                              updateEmployeeNote(employee.id, e.target.value)
                            }
                            placeholder="Add note for employee"
                            rows={2}
                            maxLength={150}
                            className="flex-1 outline-none text-gray-900 resize-none"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Manager Info */}
                <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <div className="w-11 h-11 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 font-medium uppercase">
                      Recording Manager
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {userData?.name || "You"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-green-700">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* General Note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                General Note
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                <FileText className="w-5 h-5 text-gray-600 mt-1" />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add general note (optional)"
                  rows={3}
                  maxLength={200}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
            </div>

            {/* Info Card */}
            {selectedEmployees.length > 0 && (
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-200">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900 leading-relaxed">
                  {preselectedEmployees.length > 0
                    ? "Changes will update existing work entries for the selected employees."
                    : `Creating ${selectedEmployees.length} work ${
                        selectedEmployees.length === 1 ? "entry" : "entries"
                      } for ${selectedPlace?.place || "selected place"}.`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleCreateDayWork}
            disabled={!isFormValid || isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition ${
              isFormValid && !isLoading
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>
                  {preselectedEmployees.length > 0
                    ? "Update Work Entry"
                    : "Create Work Entry"}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
}
